import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  CornerDownLeft,
  FileText,
  IdCard,
  ListChecks,
  LogOut,
  MapPin,
  MessageSquare,
  Monitor,
  Moon,
  Plus,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sun,
  User,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import { isRoyalTeamDepartment, type TripScheduleItem } from "@mtform/shared";
import { useAuth } from "@/auth/AuthContext";
import { useHasPermission } from "@/auth/usePermissions";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { highlightMatch } from "@/components/booked-trips/highlight";
import { matchesSearch } from "@/components/booked-trips/search";
import { api } from "@/lib/api";
import { titleCaseName } from "@/lib/format";
import { useTodayIso } from "@/hooks/useTodayIso";
import { prettyStatus, statusBadgeClasses } from "@/lib/trip-status";
import { useTheme } from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";
import { OpenCommandMenu } from "./commandMenuContext";

// =============================================================================
// CommandMenu (⌘K / Ctrl+K)
//
// Jump to any page you can already reach, find one of today's booked trips,
// switch theme, send feedback or sign out without leaving the keyboard. It
// lists exactly what the nav and the account menu show this user, behind the
// same role / permission / Royal-team gates; Billing is never listed, and
// trips only show for people who can see Booked trips. It won't open over
// another pop-up, menu or picker, or over the "Booking your trip…" overlay,
// and it hands focus back to where you were when it closes.
// =============================================================================

type Group = "Today's trips" | "Go to" | "Admin" | "Theme" | "Account" | "Search";
const COMMAND_GROUPS: Group[] = ["Go to", "Admin", "Theme", "Account"];

interface Command {
  id: string;
  group: Group;
  label: string;
  icon: LucideIcon;
  /** Extra words it answers to ("schedule" finds Booked trips). */
  keywords?: string;
  /** Marks the page you're on, or the theme already in use. */
  current?: boolean;
  /** A trip: its pickup time and patient under the ID, and its status. */
  trip?: { name: string; time: string; status: string; bookedBy?: string };
  run: () => void;
}

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
const SHORTCUT = IS_MAC ? "⌘K" : "Ctrl K";

// What keeps the menu shut: another pop-up (Radix dialogs and popovers are
// role=dialog), an open dropdown menu or picker list, and the "Booking your
// trip…" overlay (the app's only aria-busy element).
const BLOCKERS =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [aria-busy="true"]';

/** How well a command answers the typed words (0 = not at all). Every word
 *  must appear in its text or, from three letters, be spelled out in order
 *  inside one of its words that starts with the same letter, so "boked" or
 *  "schdule" still find Booked trips. A label that starts with it ranks
 *  first. */
function score(c: Command, words: string[]): number {
  const label = c.label.toLowerCase();
  const text = `${label} ${c.group} ${c.keywords ?? ""}`.toLowerCase();
  let total = 0;
  for (const w of words) {
    if (label.startsWith(w)) total += 3;
    else if (text.includes(w)) total += 2;
    else if (w.length >= 3 && text.split(/\s+/).some((t) => t[0] === w[0] && inOrder(w, t))) {
      total += 1;
    } else return 0;
  }
  return total;
}

function inOrder(needle: string, word: string) {
  let i = 0;
  for (const ch of word) if (ch === needle[i]) i += 1;
  return i === needle.length;
}

/** 0: the trip ID or MRN matched; 1: the patient's name did; 2: only who
 *  booked it did. */
function tripTier(t: TripScheduleItem, words: string[]) {
  const idOrMrn = `${t.friendlyId} ${t.mrn ?? ""}`.toLowerCase();
  if (words.every((w) => idOrMrn.includes(w))) return 0;
  const patient = `${idOrMrn} ${t.patientName} ${t.firstName ?? ""} ${t.lastName ?? ""}`;
  return words.every((w) => patient.toLowerCase().includes(w)) ? 1 : 2;
}

function pickupMs(t: TripScheduleItem) {
  const ms = Date.parse(t.pickupAtIso ?? t.pickupTime);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/** "Sep 12, 2026 21:33" → "21:33"; empty when there's no set time (will call). */
function tripTime(t: TripScheduleItem) {
  return t.pickupTime.match(/\d{1,2}:\d{2}(?:\s?[AP]M)?/i)?.[0] ?? "";
}

export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const { pathname } = useLocation();
  const signedIn = state.status === "authenticated";
  const [open, setOpen] = useState(false);
  // Remounts the menu on every open, so it starts with an empty query and
  // the first result highlighted.
  const [session, setSession] = useState(0);
  // Where focus was before it opened, handed back when it closes.
  const returnFocus = useRef<HTMLElement | null>(null);
  // Whether the last Cmd/Ctrl+K press opened or closed the menu.
  const claimedKey = useRef(false);

  const openMenu = useCallback(() => {
    if (document.querySelector(BLOCKERS)) return false;
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSession((n) => n + 1);
    setOpen(true);
    return true;
  }, []);

  // A page change (the browser's Back, say) closes it.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!signedIn) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Cmd+K on a Mac, where Ctrl+K deletes to the end of the line; Ctrl+K elsewhere.
      const mod = IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
      if (!mod || e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== "k" && e.code !== "KeyK") return;
      // Holding the keys down repeats them: keep what the first press did
      // (no flicker), and keep the browser off them if that press was ours.
      if (e.repeat) {
        if (claimedKey.current) e.preventDefault();
        return;
      }
      // Only claim the key when the menu actually opens or closes.
      if (open) {
        e.preventDefault();
        setOpen(false);
        claimedKey.current = true;
      } else {
        claimedKey.current = openMenu();
        if (claimedKey.current) e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [signedIn, open, openMenu]);

  return (
    <OpenCommandMenu.Provider value={signedIn ? openMenu : null}>
      {children}
      {signedIn ? (
        <CommandMenu key={session} open={open} onOpenChange={setOpen} returnFocus={returnFocus} />
      ) : null}
    </OpenCommandMenu.Provider>
  );
}

function CommandMenu({
  open,
  onOpenChange,
  returnFocus,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  returnFocus: React.MutableRefObject<HTMLElement | null>;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const canViewBooked = useHasPermission("viewBookedTrips");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const commands = useCommands(setFeedbackOpen);
  const [query, setQuery] = useState("");
  // The highlight follows a row, not a position, so trips arriving mid-search
  // can't slide something else under it before Enter.
  const [activeId, setActiveId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // A command that goes somewhere (a page, a trip, the feedback form) takes
  // focus with it; closing any other way hands focus back to where it was.
  const focusMovesOn = useRef(false);
  const listId = useId();
  const optionId = (i: number) => `${listId}-option-${i}`;

  // Today's booked trips answer a search of two or more characters, for
  // people who can see Booked trips. It's the Booked trips page's own query
  // (same key), so the two share one cached copy of the day.
  // Follows the clock, so a menu left mounted past midnight searches the
  // new day.
  const today = useTodayIso();
  const q = query.trim().toLowerCase();
  const tripsWanted = canViewBooked && q.length >= 2;
  const schedule = useQuery({
    queryKey: ["schedule", today],
    queryFn: ({ signal }) => api.trip.schedule({ date: today }, signal),
    enabled: open && tripsWanted,
    staleTime: 30 * 1000,
  });

  const { groups, results } = useMemo(() => {
    const words = q.split(/\s+/).filter(Boolean);
    const scored = commands
      .map((c) => ({ c, s: words.length ? score(c, words) : 1 }))
      .filter((x) => x.s > 0);
    const best = (g: Group) =>
      Math.max(0, ...scored.filter((x) => x.c.group === g).map((x) => x.s));
    // Groups in order of their best match (ties keep the usual order), so a
    // label that starts with what you typed isn't buried under a looser
    // match in an earlier group.
    const commandGroups = [...COMMAND_GROUPS].sort((a, b) => best(b) - best(a));
    const commandRows = commandGroups.flatMap((g) =>
      scored
        .filter((x) => x.c.group === g)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.c),
    );
    if (!tripsWanted) return { groups: commandGroups, results: commandRows };

    // The search goes over in the navigation state, not the URL, so patient
    // names and MRNs stay out of the address bar and browser history. Already
    // on Booked trips, it replaces the entry rather than stacking a second one.
    const openOnBooked = (search: string) => () =>
      navigate("/booked", { state: { search, date: today }, replace: pathname === "/booked" });
    // Best trips first: a trip ID or MRN match, then the patient's name, then
    // trips found only through who booked them; earliest pickup first in each.
    const ranked = (schedule.data?.trips ?? [])
      .filter((t) => matchesSearch(t, q))
      .map((t) => ({ t, tier: tripTier(t, words) }))
      .sort((a, b) => a.tier - b.tier || pickupMs(a.t) - pickupMs(b.t))
      .slice(0, 5);
    const trips: Command[] = ranked.map(({ t, tier }) => ({
      id: `trip-${t.friendlyId}`,
      group: "Today's trips",
      label: t.friendlyId,
      icon: CalendarClock,
      trip: {
        name: titleCaseName(t.patientName),
        time: tripTime(t),
        status: t.status,
        bookedBy: tier === 2 ? t.requestedBy : undefined,
      },
      run: openOnBooked(t.friendlyId),
    }));
    const searchToday: Command = {
      id: "search-today",
      group: "Search",
      label: `Search today's trips for “${query.trim()}”`,
      icon: Search,
      run: openOnBooked(query.trim()),
    };
    // Trips found by their ID, MRN or patient lead; ones found only through
    // who booked them follow the pages.
    const order: Group[] = ranked.some((r) => r.tier < 2)
      ? ["Today's trips", ...commandGroups, "Search"]
      : [...commandGroups, "Today's trips", "Search"];
    const all = [...trips, ...commandRows, searchToday];
    return { groups: order, results: order.flatMap((g) => all.filter((c) => c.group === g)) };
  }, [commands, q, query, tripsWanted, schedule.data, navigate, today, pathname]);

  const found = results.findIndex((c) => c.id === activeId);
  const active = found === -1 ? 0 : found;

  useEffect(() => setActiveId(null), [query]);
  useEffect(() => {
    const option = document.getElementById(optionId(active));
    option?.scrollIntoView?.({ block: "nearest" });
    // The first option of a group brings its heading into view too (and the
    // very first, the top of the list).
    const heading = option?.previousElementSibling;
    if (active === 0) listRef.current?.scrollTo?.({ top: 0 });
    else if (heading?.getAttribute("role") === "presentation") {
      heading.scrollIntoView?.({ block: "nearest" });
    }
    // optionId only depends on listId, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function run(c: Command | undefined) {
    if (!c) return;
    focusMovesOn.current = c.group !== "Theme";
    onOpenChange(false);
    c.run();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Keys working an input method (Chinese, Japanese…) belong to it: the
    // Enter that confirms the typed text mustn't also run a command.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (!results.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActiveId(results[(active + step + results.length) % results.length]!.id);
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[active]);
    }
  }

  const tripsLoading = tripsWanted && schedule.isLoading;
  const tripsFailed = tripsWanted && schedule.isError;
  const tripsNote = tripsLoading
    ? "Searching today's trips…"
    : tripsFailed
      ? "Couldn't load today's trips"
      : "";

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm motion-safe:data-[state=open]:animate-overlay-in" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            // Escape that cancels what an input method is composing
            // (Chinese, Japanese…) belongs to the search box, not the menu.
            onEscapeKeyDown={(e) => {
              if (e.isComposing || e.keyCode === 229) e.preventDefault();
            }}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            onCloseAutoFocus={(e) => {
              // Radix would focus a trigger this menu doesn't have (so,
              // <body>); hand focus back to where the user was instead.
              e.preventDefault();
              const el = returnFocus.current;
              if (!focusMovesOn.current && el?.isConnected) el.focus();
            }}
            // Anchored near the top, not centred, so the box doesn't jump as
            // the list shrinks while you type.
            className="fixed left-1/2 top-[12vh] z-50 flex max-h-[min(34rem,76vh)] w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl motion-safe:data-[state=open]:animate-palette-in"
          >
            <DialogPrimitive.Title className="sr-only">Command menu</DialogPrimitive.Title>
            <div className="flex items-center gap-3 border-b border-border/70 px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                // Short enough to show whole from a 375px-wide phone up.
                placeholder={
                  canViewBooked ? "Search pages, actions and trips…" : "Search pages and actions…"
                }
                aria-label={
                  canViewBooked ? "Search pages, actions and trips" : "Search pages and actions"
                }
                role="combobox"
                aria-expanded={results.length > 0}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={results.length ? optionId(active) : undefined}
                autoComplete="off"
                spellCheck={false}
                className="h-14 w-full min-w-0 text-ellipsis bg-transparent text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <Kbd>esc</Kbd>
            </div>
            {tripsNote ? (
              <p
                aria-hidden="true"
                className="flex items-center gap-3 px-5 pb-1 pt-3 text-sm text-muted-foreground"
              >
                <CalendarClock className="h-4 w-4 shrink-0" />
                {tripsNote}
              </p>
            ) : null}
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label="Commands"
              className="min-h-0 flex-1 overflow-y-auto p-2 empty:p-0"
            >
              {groups.map((group) => {
                const items = results.filter((c) => c.group === group);
                if (!items.length) return null;
                return (
                  <div key={group} role="presentation">
                    <div
                      role="presentation"
                      className="px-3 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {group}
                    </div>
                    {items.map((c) => {
                      const i = results.indexOf(c);
                      const Icon = c.icon;
                      const isActive = i === active;
                      // On the card's own colour, so the highlighted row
                      // doesn't dim the status (it reads as on the list).
                      const statusPill = (placement: string) =>
                        c.trip ? (
                          <span className={cn("shrink-0 rounded-full bg-card", placement)}>
                            <span
                              className={cn(
                                "block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide max-sm:px-1.5",
                                statusBadgeClasses(c.trip.status),
                              )}
                            >
                              {prettyStatus(c.trip.status)}
                            </span>
                          </span>
                        ) : null;
                      return (
                        <div
                          key={c.id}
                          id={optionId(i)}
                          role="option"
                          aria-selected={isActive}
                          // mousemove, not mouseenter: the list scrolling under
                          // a still pointer mustn't steal the keyboard's spot.
                          onMouseMove={() => setActiveId(c.id)}
                          onClick={() => run(c)}
                          className={cn(
                            "flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground/85",
                            isActive && "bg-foreground/[0.07] text-foreground",
                          )}
                        >
                          <Icon
                            className={cn(
                              "h-4 w-4 shrink-0 text-muted-foreground",
                              isActive && "text-foreground/80",
                            )}
                            aria-hidden="true"
                          />
                          {c.trip ? (
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="truncate font-mono text-[13px]">
                                  {highlightMatch(c.label, q)}
                                </span>
                                {/* Phone width: the status sits by the ID (under
                                    it if there's no room), so a long one can't
                                    squeeze the ID or the name. */}
                                {statusPill("sm:hidden")}
                              </span>
                              {/* Time first, so a narrow screen trims the end of
                                  a long name rather than the pickup time. */}
                              <span className="truncate text-xs text-muted-foreground">
                                {c.trip.time ? `${c.trip.time} · ` : ""}
                                {highlightMatch(c.trip.name, q)}
                                {c.trip.bookedBy ? (
                                  <> · Booked by {highlightMatch(c.trip.bookedBy, q)}</>
                                ) : null}
                              </span>
                            </span>
                          ) : (
                            <span className="min-w-0 flex-1 truncate">{c.label}</span>
                          )}
                          {statusPill("max-sm:hidden")}
                          {c.current ? (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {c.group === "Theme" ? "In use" : "Current page"}
                            </span>
                          ) : null}
                          {isActive ? (
                            // The footer already says Enter opens; a phone-width
                            // row needs the room more.
                            <CornerDownLeft
                              className="h-3.5 w-3.5 shrink-0 text-muted-foreground max-sm:hidden"
                              aria-hidden="true"
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {!results.length ? (
              <p
                aria-hidden="true"
                className="px-3 py-10 text-center text-sm text-muted-foreground"
              >
                No results for “{query.trim()}”
              </p>
            ) : null}
            <p role="status" className="sr-only">
              {tripsNote || (results.length ? "" : `No results for “${query.trim()}”`)}
            </p>
            <div className="flex items-center gap-4 border-t border-border/70 bg-foreground/[0.025] px-4 py-2.5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                to move
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Kbd>↵</Kbd>
                to open
              </span>
              <span className="ml-auto hidden items-center gap-1.5 sm:inline-flex">
                <Kbd>{SHORTCUT}</Kbd>
                to open or close
              </span>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}

/** Everything this user can already reach from the nav and account menu. */
function useCommands(openFeedback: (open: boolean) => void): Command[] {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const here = `${pathname}${search}${hash}`;
  const { state, signOut } = useAuth();
  const { preference, setPreference } = useTheme();
  const canViewBooked = useHasPermission("viewBookedTrips");
  const user = state.status === "authenticated" ? state.user : null;

  return useMemo(() => {
    if (!user) return [];
    const isAdmin = user.role === "admin" || user.role === "super_admin";
    const isRoyalTeam = isAdmin && isRoyalTeamDepartment(user.department);
    const page = (
      id: string,
      group: Group,
      label: string,
      to: string,
      icon: LucideIcon,
      keywords?: string,
    ): Command => ({
      id,
      group,
      label,
      icon,
      keywords,
      current: pathname === to,
      // Like the nav links: choosing exactly where you are replaces its
      // history entry rather than stacking a second one for Back to step
      // through.
      run: () => navigate(to, { replace: here === to }),
    });
    const theme = (
      value: "light" | "dark" | "system",
      label: string,
      icon: LucideIcon,
    ): Command => ({
      id: `theme-${value}`,
      group: "Theme",
      label,
      icon,
      keywords: "appearance mode colour color",
      current: preference === value,
      run: () => setPreference(value),
    });
    const list: Command[] = [
      page("new-trip", "Go to", "New trip", "/", Plus, "book request form"),
      ...(canViewBooked
        ? [page("booked", "Go to", "Booked trips", "/booked", CalendarDays, "schedule list today")]
        : []),
      page("account", "Go to", "Account settings", "/account", User, "profile password"),
    ];
    if (isAdmin) {
      list.push(
        page("admin-users", "Admin", "Users", "/admin/users", UsersIcon, "people invite staff"),
        page("admin-bookings", "Admin", "Bookings", "/admin/bookings", ListChecks, "trips all"),
        page("admin-members", "Admin", "Members", "/admin/members", IdCard, "patients mrn"),
        page("admin-departments", "Admin", "Departments", "/admin/departments", Building2),
        page("admin-locations", "Admin", "Locations", "/admin/locations", MapPin, "addresses"),
        page(
          "admin-audit",
          "Admin",
          "Audit log",
          "/admin/audit",
          ClipboardList,
          "history activity",
        ),
        page("admin-reports", "Admin", "Reports", "/admin/reports", FileText, "monthly export"),
      );
      if (user.role === "super_admin") {
        list.push(
          page(
            "admin-permissions",
            "Admin",
            "Permissions",
            "/admin/permissions",
            ShieldCheck,
            "roles access",
          ),
        );
      }
      // Royal-internal, as in the account menu. Billing stays out on purpose.
      if (isRoyalTeam) {
        list.push(page("admin-tms", "Admin", "TMS Cleanup", "/admin/tms-cleanup", RefreshCcw));
      }
    }
    list.push(
      theme("light", "Light theme", Sun),
      theme("dark", "Dark theme", Moon),
      theme("system", "Match system theme", Monitor),
      {
        id: "feedback",
        group: "Account",
        label: "Send feedback",
        icon: MessageSquare,
        keywords: "bug idea help",
        run: () => openFeedback(true),
      },
      {
        id: "sign-out",
        group: "Account",
        label: "Sign out",
        icon: LogOut,
        keywords: "log out logout",
        run: () => {
          void signOut().then(() => navigate("/login", { replace: true }));
        },
      },
    );
    return list;
  }, [
    user,
    pathname,
    here,
    preference,
    canViewBooked,
    navigate,
    setPreference,
    signOut,
    openFeedback,
  ]);
}

/** The header button that opens the menu (shown from 1024px up). */
export function CommandMenuTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-keyshortcuts="Meta+K Control+K"
      className="hidden h-9 shrink-0 items-center gap-2 rounded-lg border border-border/70 bg-foreground/[0.03] pl-3 pr-1.5 text-sm text-muted-foreground transition hover:border-foreground/20 hover:bg-foreground/[0.06] hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:inline-flex"
    >
      <Search className="h-4 w-4" aria-hidden="true" />
      <span className="pr-6">Search…</span>
      <Kbd>{SHORTCUT}</Kbd>
    </button>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded border border-border bg-foreground/[0.04] px-1 font-sans text-[11px] font-medium text-muted-foreground">
      {children}
    </kbd>
  );
}
